import React, { useEffect, useRef, useState } from "react";
import {
  Menu,
  X,
  Send,
  Bot,
  User,
  Loader2,
  AlertCircle,
  ChevronUp,
  Code,
  Database,
  Calendar,
  Globe,
  Shield,
  RefreshCw,
  CheckCircle,
  XCircle,
} from "lucide-react";
import MessageContent from "./MessageContent";
import Logo from "../../assets/logo.png";

export default function ChatArea({
  messages = [],
  currentSession,
  isLoading = false,
  error = null,
  isSidebarOpen = true,
  onToggleSidebar,
  onSendMessage,
  onLoadMore,
}) {
  const [inputMessage, setInputMessage] = useState("");
  const [useWebSearch, setUseWebSearch] = useState(false);
  // 알림(ON/OFF 모두) 표시용 상태 — 알림에만 '보안모드' 워딩 사용
  const [showSecurityWarning, setShowSecurityWarning] = useState(false);
  const [securityNoticeMode, setSecurityNoticeMode] = useState("on"); // "on" | "off"
  const [showScrollButton, setShowScrollButton] = useState(false);

  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const textareaRef = useRef(null);

  const safeMessages = Array.isArray(messages) ? messages : [];

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleScroll = () => {
    if (messagesContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } =
        messagesContainerRef.current;
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
      setShowScrollButton(!isNearBottom);
      if (scrollTop === 0 && safeMessages.length >= 20) {
        onLoadMore && onLoadMore();
      }
    }
  };

  useEffect(() => {
    if (safeMessages.length === 0) return;
    const lastMessage = safeMessages[safeMessages.length - 1];
    if (lastMessage?.role === "model" || lastMessage?.role === "user") {
      scrollToBottom();
    }
  }, [safeMessages]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      const newHeight = Math.min(textareaRef.current.scrollHeight, 200);
      textareaRef.current.style.height = `${newHeight}px`;
    }
  }, [inputMessage]);

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;
    const message = inputMessage;
    setInputMessage("");
    if (textareaRef.current) textareaRef.current.style.height = "auto";
    await onSendMessage?.(message, currentSession?.id, {
      searchOnly: useWebSearch,
    });
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // 헤더 우측 상단 토글(크게) — 켜도/꺼도 5초 알림
  const handleToggleWebSearch = () => {
    setUseWebSearch((prev) => {
      const next = !prev;
      setSecurityNoticeMode(next ? "on" : "off");
      setShowSecurityWarning(true);
      setTimeout(() => setShowSecurityWarning(false), 5000);
      return next;
    });
  };

  const getFunctionIcon = (functionName = "") => {
    const n = (functionName || "").toLowerCase();
    if (n.includes("calendar") || n.includes("task"))
      return <Calendar className="w-3.5 h-3.5" />;
    if (n.includes("financial") || n.includes("table"))
      return <Database className="w-3.5 h-3.5" />;
    if (n.includes("search")) return <Globe className="w-3.5 h-3.5" />;
    return <Code className="w-3.5 h-3.5" />;
  };

  const getFunctionBadgeLabel = (functionName = "") => {
    const n = (functionName || "").toLowerCase();
    if (n.includes("calendar") || n.includes("task")) return "캘린더 연동";
    if (n.includes("financial") || n.includes("table")) return "재무 데이터";
    if (n.includes("search")) return "웹 검색";
    return "내부 함수";
  };

  return (
    <div className="flex-1 flex flex-col bg-white">
      {/* 헤더: 경계선 제거 */}
      <div className="bg-white">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={onToggleSidebar}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              {isSidebarOpen ? (
                <X className="w-5 h-5" />
              ) : (
                <Menu className="w-5 h-5" />
              )}
            </button>
            <div className="flex items-center gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-lg font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
                    ELVA 통합 워크스페이스 비서
                  </h1>
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-[10px] px-2 py-0.5 bg-gradient-to-r from-violet-600 to-indigo-600 text-white rounded-full font-medium shadow-sm">
                    Model v2.5
                  </span>
                  <span className="text-[10px] px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full">
                    Build 2025.0722
                  </span>
                  {currentSession && (
                    <span className="text-xs text-gray-500 ml-2">
                      {currentSession.session_name || "새 대화"}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* 웹검색 모드 토글 (오른쪽 상단, 크게) */}
          <button
            onClick={handleToggleWebSearch}
            className={`inline-flex items-center gap-1.5 rounded-full border transition-all
              ${
                useWebSearch
                  ? "bg-indigo-600 text-white border-indigo-600"
                  : "bg-white text-gray-800 border-gray-300 hover:bg-gray-50"
              }
              px-4 py-2 text-sm font-semibold`}
            aria-pressed={useWebSearch}
            title="웹검색모드 토글"
          >
            <Globe className="w-4 h-4" />
            {useWebSearch ? "웹검색모드 ON" : "웹검색모드 OFF"}
          </button>
        </div>

        {/* 알림 배너 (켜도/꺼도 5초간 노출) — 알림에만 '보안모드' 사용 */}
        {showSecurityWarning && (
          <div
            className={`px-4 py-2 ${
              securityNoticeMode === "on" ? "bg-amber-50" : "bg-emerald-50"
            }`}
          >
            <div
              className={`flex items-center gap-2 text-sm ${
                securityNoticeMode === "on"
                  ? "text-amber-800"
                  : "text-emerald-800"
              }`}
            >
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {securityNoticeMode === "on" ? (
                <span>
                  웹검색모드 ON — <b>보안모드</b> 활성화: 내부 데이터 접근이
                  제한됩니다.
                </span>
              ) : (
                <span>
                  웹검색모드 OFF — <b>보안모드</b> 해제: 내부 데이터 모드로
                  전환되었습니다.
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* 메시지 영역 */}
      <div
        ref={messagesContainerRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto relative"
      >
        <div className="max-w-3xl mx-auto py-8">
          {safeMessages.length === 0 ? (
            <div className="text-center py-16 px-4">
              <img src={Logo} alt="elva로고" className="w-24 h-24 mx-auto" />

              <h2 className="text-2xl font-semibold text-gray-900 mb-2">
                무엇을 도와드릴까요?
              </h2>
              <p className="text-gray-600 mb-8">
                캘린더 조회, 재무 데이터 확인, 일정 관리, 웹검색 등을
                도와드립니다.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-lg mx-auto">
                <button
                  onClick={() => setInputMessage("오늘 일정 알려줘")}
                  className="text-left p-4 rounded-xl border border-gray-200 hover:border-blue-300 hover:bg-blue-50/50 transition-all group"
                >
                  <Calendar className="w-5 h-5 text-blue-600 mb-2 group-hover:scale-110 transition-transform" />
                  <div className="text-sm font-medium">오늘 일정 확인</div>
                  <div className="text-xs text-gray-500">
                    캘린더 일정을 조회합니다
                  </div>
                </button>
                <button
                  onClick={() => setInputMessage("재무 테이블 목록 보여줘")}
                  className="text-left p-4 rounded-xl border border-gray-200 hover:border-emerald-300 hover:bg-emerald-50/50 transition-all group"
                >
                  <Database className="w-5 h-5 text-emerald-600 mb-2 group-hover:scale-110 transition-transform" />
                  <div className="text-sm font-medium">재무 데이터 조회</div>
                  <div className="text-xs text-gray-500">
                    테이블 데이터를 확인합니다
                  </div>
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-6 px-4">
              {safeMessages.map((message, index) => {
                // 빈 응답 체크
                const isEmptyResponse =
                  message?.role === "model" &&
                  !message?.isStreaming &&
                  (!message?.message || message?.message.trim() === "");

                return (
                  <div
                    key={message?.id || index}
                    className={`${
                      message?.role === "user" ? "flex justify-end" : ""
                    }`}
                  >
                    {message?.role === "user" ? (
                      <div className="max-w-xl">
                        <div className="bg-gradient-to-r from-gray-800 to-gray-900 text-white px-4 py-2.5 rounded-2xl shadow-md">
                          <MessageContent content={message?.message || ""} />
                        </div>
                        {message?.created_at && (
                          <div className="text-xs text-gray-400 mt-1 px-1 text-right">
                            {new Date(message.created_at).toLocaleTimeString(
                              "ko-KR",
                              {
                                hour: "2-digit",
                                minute: "2-digit",
                              }
                            )}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="max-w-3xl">
                        {message?.functionCalls &&
                          Array.isArray(message.functionCalls) &&
                          message.functionCalls.length > 0 && (
                            <div className="mb-3 flex flex-wrap gap-2">
                              {message.functionCalls.map((fc, i) => (
                                <span
                                  key={i}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-full text-xs font-medium shadow-sm"
                                >
                                  {getFunctionIcon(fc?.name)}
                                  {getFunctionBadgeLabel(fc?.name)}
                                </span>
                              ))}
                            </div>
                          )}

                        {message?.isStreaming ? (
                          <div className="text-gray-900">
                            <MessageContent content={message?.message || ""} />
                            <span className="inline-block w-0.5 h-5 bg-blue-600 animate-pulse ml-0.5"></span>
                          </div>
                        ) : isEmptyResponse ? (
                          <div className="bg-orange-50 text-orange-900 border border-orange-200 rounded-lg p-4">
                            <div className="flex items-start gap-2">
                              <XCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                              <div className="flex-1">
                                <span className="text-sm font-medium">
                                  응답 생성에 실패했습니다
                                </span>
                                <p className="text-xs mt-1 text-orange-700">
                                  응답이 비어있습니다. 다시 시도해주세요.
                                </p>
                                {(message?.retryMessage ||
                                  message?.originalMessage) && (
                                  <button
                                    onClick={() =>
                                      onSendMessage?.(
                                        message.retryMessage ||
                                          message.originalMessage,
                                        currentSession?.id,
                                        { searchOnly: useWebSearch }
                                      )
                                    }
                                    className="mt-2 inline-flex items-center gap-1 px-3 py-1.5 bg-orange-600 hover:bg-orange-700 text-white text-sm rounded-lg transition-colors"
                                  >
                                    <RefreshCw className="w-3 h-3" />
                                    다시 시도
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        ) : message?.isError ? (
                          <div className="bg-red-50 text-red-900 border border-red-200 rounded-lg p-4">
                            <div className="flex items-start gap-2">
                              <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                              <div className="flex-1">
                                <span className="text-sm font-medium">
                                  {message?.message || "오류가 발생했습니다"}
                                </span>
                                {message?.retryMessage && (
                                  <button
                                    onClick={() =>
                                      onSendMessage?.(
                                        message.retryMessage,
                                        currentSession?.id,
                                        {
                                          searchOnly: useWebSearch,
                                        }
                                      )
                                    }
                                    className="mt-2 inline-flex items-center gap-1 px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-sm rounded-lg transition-colors"
                                  >
                                    <RefreshCw className="w-3 h-3" />
                                    다시 시도
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="text-gray-900">
                            <MessageContent content={message?.message || ""} />
                          </div>
                        )}

                        {!message?.isStreaming && message?.created_at && (
                          <div className="text-xs text-gray-400 mt-2">
                            {new Date(message.created_at).toLocaleTimeString(
                              "ko-KR",
                              {
                                hour: "2-digit",
                                minute: "2-digit",
                              }
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}

              {isLoading && !safeMessages.some((m) => m?.isStreaming) && (
                <div className="max-w-3xl">
                  <div className="flex items-center gap-2 text-gray-500">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="text-sm">생각하는 중...</span>
                  </div>
                </div>
              )}

              {error && (
                <div className="flex items-center gap-2 px-4 py-3 bg-red-50 text-red-700 rounded-lg">
                  <AlertCircle className="w-5 h-5 flex-shrink-0" />
                  <span className="text-sm">{error}</span>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {showScrollButton && (
          <button
            onClick={scrollToBottom}
            className="fixed bottom-24 right-8 p-3 bg-white border border-gray-300 rounded-full shadow-lg hover:shadow-xl transition-all"
          >
            <ChevronUp className="w-5 h-5 rotate-180" />
          </button>
        )}
      </div>

      {/* 입력 영역: 경계선 제거 */}
      <div className="bg-white">
        <div className="max-w-3xl mx-auto p-4">
          <div className="relative">
            {/* (이전 위치의 웹검색 토글/모드 텍스트는 제거) */}
            <style>{`
              .chat-input-textarea { -ms-overflow-style:none; scrollbar-width:none; }
              .chat-input-textarea::-webkit-scrollbar { display:none; width:0; height:0; }
            `}</style>
            <textarea
              ref={textareaRef}
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={
                useWebSearch
                  ? "웹에서 검색할 내용을 입력하세요..."
                  : "메시지를 입력하세요..."
              }
              className="chat-input-textarea w-full resize-none outline-none border border-gray-300 rounded-xl px-4 py-3 pr-12 text-gray-900 placeholder-gray-500 focus:border-blue-400 transition-colors"
              rows="1"
              disabled={isLoading}
              style={{
                minHeight: "48px",
                maxHeight: "200px",
                overflowY: "auto",
              }}
            />
            <button
              onClick={handleSendMessage}
              disabled={!inputMessage.trim() || isLoading}
              className={`absolute right-2 bottom-3 p-2 rounded-lg transition-all ${
                inputMessage.trim() && !isLoading
                  ? "bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:shadow-lg"
                  : "bg-gray-100 text-gray-400 cursor-not-allowed"
              }`}
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
          <div className="text-xs text-gray-500 mt-2 text-center">
            Enter로 전송, Shift+Enter로 줄바꿈
          </div>
        </div>
      </div>
    </div>
  );
}
